import { CommonModule } from '@angular/common';
import {
  Component,
  OnChanges,
  OnInit,
  SimpleChanges,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';

import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';

import { Account, AccountApi } from '@venecia/apis/identity';
import { ErrorMessageService, MessageService, TraceService } from '@venecia/services';

import { ResponsiveTable } from '../responsive-table/responsive-table';
import { Column, RowAction } from '../responsive-table/responsive-table.model';
import { Spinner } from '../spinner/spinner';

@Component({
  selector: 'app-account-grid',
  standalone: true,
  imports: [CommonModule, TranslateModule, ResponsiveTable, Spinner],
  templateUrl: './account-grid.html',
  styleUrl: './account-grid.scss',
})
export class AccountGrid implements OnInit, OnChanges {
  private readonly accountApi = inject(AccountApi);
  private readonly errorMessageService = inject(ErrorMessageService);
  private readonly messageService = inject(MessageService);
  private readonly traceService = inject(TraceService);
  private readonly translateService = inject(TranslateService);

  readonly reloadVersion = input<number>(0);
  readonly detailRequested = output<Account>();

  readonly accounts = signal<readonly Account[]>([]);
  readonly loading = signal(false);

  readonly pageNumber = signal(1);
  readonly pageSize = signal(10);
  readonly totalItems = signal(0);

  readonly trackByAccount = (_: number, account: Account) => account.id ?? account.name;

  readonly columns = computed<readonly Column<Account>[]>(() => [
    {
      id: 'logo-url',
      header: '',
      value: (account) => account['logo-url'] ?? '',
      width: 10,
      isImage: true
    },
    {
      id: 'name',
      header: this.translateService.instant('pages.accounts.fields.name'),
      value: (account) => account.name,
      width: 30
    },
    {
      id: 'web-url',
      header: this.translateService.instant('pages.accounts.fields.web-url'),
      value: (account) => account['web-url'] ?? '',
      width: 50
    },
  ]);

  readonly actions = computed<readonly RowAction<Account>[]>(() => [
    {
      id: 'details',
      label: this.translateService.instant('pages.accounts.actions.details'),
      icon: 'fa-solid fa-pen-to-square',
    },
  ]);

  ngOnInit(): void {
    void this.loadAccounts();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const reloadVersionChange = changes['reloadVersion'];
    if (!reloadVersionChange || reloadVersionChange.firstChange) {
      return;
    }

    this.pageNumber.set(1);
    void this.loadAccounts();
  }

  onActionSelected(ev: { actionId: string; item: Account }): void {
    if (ev.actionId === 'details') {
      this.detailRequested.emit(ev.item);
    }
  }

  onPageChange(page: number): void {
    if (page === this.pageNumber()) {
      return;
    }

    this.pageNumber.set(page);
    void this.loadAccounts();
  }

  private async loadAccounts(): Promise<void> {
    this.loading.set(true);

    try {
      const response = await firstValueFrom(
        this.accountApi.search({
          payload: {
            page: this.pageNumber() - 1,
            size: this.pageSize(),
          },
        }),
      );

      this.accounts.set(response.body.data ?? []);
      this.totalItems.set(response.body.total ?? 0);
    } catch (error) {
      this.traceService.internal(error, true);
      this.messageService.error(await this.errorMessageService.resolveApiError(error, 'pages.accounts.load-error'));
    } finally {
      this.loading.set(false);
    }
  }
}
